/**
 * Camada 1 · o painel de ajuda, a tela que o contrato de `index.ts` existia para servir.
 *
 * O NAMESPACE E OBRIGATORIO E VEM DE CIMA, do ProductConfig, e nunca de um padrao aqui
 * dentro. O cabecalho de `index.ts` ja diz o porque, e o componente cumpre a mesma regra:
 * sem namespace ele NAO busca, e mostra o estado `indisponivel` dizendo isso. Um padrao
 * silencioso aqui faria dois produtos verem a ajuda um do outro por esquecimento.
 *
 * O PAINEL NAO CONHECE `services/knowledge`. Ele recebe uma `FonteAjuda`, que e interface,
 * e por isso a suite consegue exercitar a tela inteira sem subir servico nenhum.
 *
 * ESTE ARQUIVO NAO DESENHA CHASSI. Cabecalho, rail e tema moram em `@complexity/layout`, e
 * o `guarda-fronteira.sh` recusa a escrita que os duplicasse aqui. O painel e a PECA que
 * entra no chassi, e nao um pedaco de chassi novo.
 */

import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { EstadoErro, EstadoVazio, IconeFechar, Markdown, useIdiomaVivo } from '@complexity/ui';
import { t } from '@complexity/i18n';
import { buscarAjuda, type ArtigoAjuda, type EstadoAjuda, type FonteAjuda } from '../index.js';

export interface PainelAjudaProps {
  /** Vem do ProductConfig do produto. Ver o cabecalho: obrigatorio, e sem padrao. */
  readonly namespace: string;
  readonly fonte: FonteAjuda;
  /** Fecha o painel. O chassi decide onde o painel mora; ele so avisa que terminou. */
  readonly aoFechar: () => void;
  /**
   * Abre a conversa com a operacao, quando o produto tem esse caminho ligado.
   *
   * OPCIONAL DE PROPOSITO: produto sem canal de mensagem nao mostra o botao. Oferecer
   * "falar com a gente" onde ninguem responde e pior que nao oferecer, porque promete.
   */
  readonly aoAbrirConversa?: (termoQueFalhou: string) => void;
}

/**
 * O tempo de espera antes de buscar, em milissegundos.
 *
 * 320ms porque abaixo disso a busca dispara no meio de uma palavra e a lista pisca a cada
 * tecla; acima, a pessoa percebe a demora e digita de novo achando que nao funcionou.
 */
const ESPERA_MS = 320;

export function PainelAjuda({ namespace, fonte, aoFechar, aoAbrirConversa }: PainelAjudaProps) {
  /**
   * O IDIOMA VIVO, PRIMEIRA linha do corpo.
   *
   * O VALOR PASSOU A SER USADO com o ADR 0060: ele vai em toda leitura de conhecimento, e a
   * troca refaz a busca e o artigo aberto. O efeito de antes continua valendo: o estado
   * interno dele muda na troca, e isso obriga o React a redesenhar a gaveta com o dicionario
   * novo.
   *
   * SEM ELE a gaveta continua em portugues depois de a pessoa escolher EN, ate a proxima
   * navegacao, e ela flutua sobre um chassi que JA trocou: as duas metades da mesma tela em
   * idiomas diferentes, lado a lado.
   */
  const idioma = useIdiomaVivo();

  const [termo, definirTermo] = useState('');
  const [estado, definirEstado] = useState<EstadoAjuda>({ tipo: 'ocioso' });
  const [aberto, definirAberto] = useState<ArtigoAjuda | null>(null);
  const [corpo, definirCorpo] = useState<string | null>(null);

  const idBusca = useId();
  const campoRef = useRef<HTMLInputElement>(null);

  /**
   * O contador da busca em curso.
   *
   * SEM ELE A LISTA PODE MOSTRAR O RESULTADO ERRADO: duas buscas disparadas em sequencia
   * podem voltar fora de ordem, e a mais lenta sobrescreveria a mais recente. A pessoa
   * veria o resultado de "fer" depois de ja ter digitado "ferias". Guardar o numero da
   * ultima disparada e comparar na volta e o que descarta a resposta que envelheceu.
   */
  const buscaAtual = useRef(0);

  // O foco vai para o campo ao abrir. Painel que abre sem foco obriga quem usa teclado a
  // navegar ate o campo antes de digitar, que e o gesto que a pessoa veio fazer.
  useEffect(() => {
    campoRef.current?.focus();
  }, []);

  // Escape fecha. E o gesto que todo mundo tenta primeiro, e nao custa nada atender.
  useEffect(() => {
    function aoTeclar(evento: KeyboardEvent) {
      if (evento.key !== 'Escape') return;
      // Escape com artigo aberto volta para a lista, e nao fecha o painel inteiro: fechar
      // tudo perderia a busca que a pessoa acabou de fazer.
      if (aberto !== null) {
        definirAberto(null);
        definirCorpo(null);
        return;
      }
      aoFechar();
    }
    document.addEventListener('keydown', aoTeclar);
    return () => document.removeEventListener('keydown', aoTeclar);
  }, [aberto, aoFechar]);

  // A busca, atrasada. O `termo` vazio volta ao ocioso sem chamar a fonte: buscar por nada
  // devolveria a base inteira, que nao e o que a pessoa pediu.
  useEffect(() => {
    if (termo.trim() === '') {
      definirEstado({ tipo: 'ocioso' });
      return;
    }

    const meuNumero = ++buscaAtual.current;
    definirEstado({ tipo: 'buscando' });

    const relogio = setTimeout(() => {
      void buscarAjuda(fonte, namespace, termo, idioma).then((novo) => {
        // Chegou fora de ordem: descarta. Ver o comentario de `buscaAtual`.
        if (meuNumero !== buscaAtual.current) return;
        definirEstado(novo);
        // O artigo aberto ganha o titulo no idioma novo, achado pelo id (ADR 0060). Sem isto,
        // trocar PT e EN com um artigo aberto deixaria o titulo antigo sobre o corpo novo.
        if (novo.tipo === 'resultado') {
          definirAberto((atual) =>
            atual ? (novo.artigos.find((a) => a.id === atual.id) ?? atual) : atual,
          );
        }
      });
    }, ESPERA_MS);

    return () => clearTimeout(relogio);
    // O idioma nas dependencias refaz a busca na troca PT e EN, com o resultado ja na tela.
  }, [termo, namespace, fonte, idioma]);

  /*
   * O CORPO DO ARTIGO ABERTO SAIU DO CLIQUE E VIROU EFEITO, chaveado pelo id e pelo idioma.
   * No clique a leitura acontecia uma vez so, e a troca de idioma nao tinha como alcanca-la:
   * o corpo ficaria em portugues depois de a pessoa escolher EN com o artigo aberto.
   */
  const idAberto = aberto?.id ?? null;
  useEffect(() => {
    if (idAberto === null) return;
    let vivo = true;
    definirCorpo(null);
    void fonte
      .obter(namespace, idAberto, idioma)
      .then((texto) => {
        if (vivo) definirCorpo(texto);
      })
      // A falha ao abrir NAO derruba a lista: quem volta continua com a busca feita. O
      // corpo nulo vira a mensagem de indisponivel logo abaixo.
      .catch(() => {
        if (vivo) definirCorpo(null);
      });
    return () => {
      vivo = false;
    };
  }, [fonte, namespace, idAberto, idioma]);

  const abrirArtigo = useCallback((artigo: ArtigoAjuda) => {
    definirAberto(artigo);
  }, []);

  return (
    <div className="flex h-full flex-col gap-cx-4 p-cx-4">
      <div className="flex items-center justify-between gap-cx-3">
        <h2 className="text-cx-lg font-semibold text-texto">{t('Ajuda')}</h2>
        <button
          type="button"
          onClick={aoFechar}
          // 44px nos DOIS eixos: e o minimo de alvo de toque desta casa, e um botao de
          // fechar pequeno no celular e o que faz a pessoa fechar o painel sem querer.
          className="min-h-[44px] min-w-[44px] rounded-cx-md text-texto-suave hover:text-texto"
          aria-label={t('Fechar a ajuda')}
        >
          {/* Icone DESENHADO, e nao o simbolo Unicode: a regra dura da plataforma
              trata `x` como emoji, porque ele muda de desenho por fonte instalada. */}
          <IconeFechar />
        </button>
      </div>

      {aberto === null ? (
        <>
          <div>
            <label htmlFor={idBusca} className="mb-cx-1 block text-cx-sm text-texto-suave">
              {t('O que você procura?')}
            </label>
            <input
              id={idBusca}
              ref={campoRef}
              type="search"
              value={termo}
              onChange={(e) => definirTermo(e.target.value)}
              placeholder={t('Buscar na ajuda')}
              className="min-h-[44px] w-full rounded-cx-md border border-borda bg-superficie px-cx-3 text-cx-base text-texto"
            />
          </div>

          {/* `aria-live` para quem usa leitor de tela saber que a lista mudou sem o foco
              ter saido do campo. Sem isso, a busca acontece em silencio para essa pessoa. */}
          <div className="min-h-0 flex-1 overflow-y-auto" aria-live="polite">
            {estado.tipo === 'ocioso' && (
              <p className="text-cx-sm text-texto-suave">
                {t('Digite para buscar nos artigos deste produto.')}
              </p>
            )}

            {estado.tipo === 'buscando' && (
              <p className="text-cx-sm text-texto-suave">{t('Buscando…')}</p>
            )}

            {/* Os dois estados que se parecem e sao opostos, e por isso sao componentes
                diferentes: `indisponivel` e "nao consegui olhar"; a lista vazia e "olhei e
                nao ha". Confundir os dois faz a pessoa procurar defeito na propria busca. */}
            {estado.tipo === 'indisponivel' && (
              /* O MOTIVO passa por `t()` aqui, e nao onde ele e criado (`buscarAjuda`, no
                 `index.ts`): criado traduzido, ele congelaria o idioma do instante da falha e
                 a troca nao o alcancaria. E `t()` devolve a propria chave quando nao ha
                 entrada, entao mensagem vinda do servidor atravessa intacta. */
              <EstadoErro titulo={t('A ajuda não respondeu')} mensagem={t(estado.motivo)}>
                {t('O conteúdo existe, mas não conseguimos buscá-lo agora.')}
              </EstadoErro>
            )}

            {estado.tipo === 'resultado' && estado.artigos.length === 0 && (
              <EstadoVazio
                titulo={t('Nenhum artigo para esse termo')}
                acao={
                  aoAbrirConversa && (
                    <button
                      type="button"
                      onClick={() => aoAbrirConversa(termo)}
                      className={
                        // VIDRO, pelo padrao da plataforma fechado em 09/09/2026. Este botao
                        // NAO usa a peca `Botao` de `@complexity/ui`, entao ele nao herdou a
                        // troca automaticamente: e exatamente o tipo de lugar onde um padrao
                        // vira "padronizado onde alguem lembrou".
                        'min-h-[44px] rounded-cx-md px-cx-4 text-cx-sm font-medium ' +
                        'bg-marca/15 text-marca border border-marca/50 hover:bg-marca/25 ' +
                        'dark:bg-marca/20 dark:border-marca/40 ' +
                        'backdrop-blur-vidro backdrop-saturate-vidro'
                      }
                    >
                      {t('Falar com a nossa equipe')}
                    </button>
                  )
                }
              >
                {t('A busca funcionou e não encontrou nada com esse termo. Tente outra palavra.')}
              </EstadoVazio>
            )}

            {estado.tipo === 'resultado' && estado.artigos.length > 0 && (
              <ul className="flex flex-col gap-cx-2">
                {estado.artigos.map((artigo) => (
                  <li key={artigo.id}>
                    <button
                      type="button"
                      onClick={() => abrirArtigo(artigo)}
                      className="min-h-[44px] w-full rounded-cx-md border border-borda p-cx-3 text-left hover:border-marca"
                    >
                      <span className="block text-cx-sm font-medium text-texto">
                        {artigo.titulo}
                      </span>
                      <span className="mt-cx-1 block text-cx-xs text-texto-suave">
                        {artigo.resumo}
                      </span>
                      {/* A queda para o original e dita na lista (ADR 0060): pedido em ingles,
                          artigo sem traducao chega em portugues, e a pessoa precisa saber antes
                          de abrir. `idioma` ausente (fonte em memoria) nao tem o que avisar. */}
                      {artigo.idioma !== undefined && artigo.idioma !== idioma ? (
                        <span className="mt-cx-1 block text-cx-xs text-texto-fraco">
                          {t('Disponível só em português')}
                        </span>
                      ) : null}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </>
      ) : (
        <div className="min-h-0 flex-1 overflow-y-auto">
          <button
            type="button"
            onClick={() => {
              definirAberto(null);
              definirCorpo(null);
            }}
            className="mb-cx-3 min-h-[44px] text-cx-sm text-marca"
          >
            {t('Voltar aos resultados')}
          </button>
          <h3 className="text-cx-base font-semibold text-texto">{aberto.titulo}</h3>
          {aberto.idioma !== undefined && aberto.idioma !== idioma ? (
            <p className="mt-cx-1 text-cx-xs text-texto-fraco">{t('Disponível só em português')}</p>
          ) : null}
          {corpo === null ? (
            <p className="mt-cx-3 text-cx-sm text-texto-suave">
              {t('Não conseguimos carregar este artigo agora.')}
            </p>
          ) : (
            /*
              O CORPO E MARKDOWN, e o campo que o traz se chama `corpo_md`
              (services/knowledge/src/artigos/artigos.controller.ts:75). Desenhado como texto
              puro, ele mostrava a marcacao na tela: medido em 09/09/2026 no artigo "Posso
              mandar dado de paciente numa mensagem de suporte", a gaveta exibia 6 titulos com
              `#`, 8 marcas de negrito e 6 linhas de tabela em pipes, sem um <strong> sequer.

              O RENDERIZADOR E O MESMO DA CENTRAL (`CentralDeAjuda.tsx:487`), e nao um segundo
              escrito aqui: as duas superficies mostram o MESMO artigo, e dois renderizadores
              divergem na primeira correcao feita de um lado so.
            */
            <div className="mt-cx-3">
              <Markdown texto={corpo} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
